import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { marketingApi } from '@/api/marketing';
import { COURSE_HREF } from '@/lib/marketingCtas';

const GRADE_OPTIONS = ['9th grade', '10th grade', '11th grade', '12th grade', 'Other'];

const TEST_DATE_OPTIONS = [
  'August 2026',
  'October 2026',
  'November 2026',
  'December 2026',
  'March 2027',
  'May 2027',
  'June 2027',
  'Not sure yet',
];

export function SatIntakeFormCard() {
  const [searchParams] = useSearchParams();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [targetTestDate, setTargetTestDate] = useState('');
  const [referralCode, setReferralCode] = useState(() => searchParams.get('ref') ?? '');
  const [website, setWebsite] = useState(''); // honeypot
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: marketingApi.submitSatIntake,
    onSuccess: () => setSubmitted(true),
    onError: (error: Error) => toast.error(error.message),
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim()) {
      toast.error('Name and email are required.');
      return;
    }
    mutation.mutate({
      fullName: fullName.trim(),
      email: email.trim(),
      phone: phone.trim() || undefined,
      gradeLevel: gradeLevel || undefined,
      targetTestDate: targetTestDate || undefined,
      referralCode: referralCode.trim() || undefined,
      website: website || undefined,
    });
  };

  return (
    <Card id="sat-intake-form" className="w-full max-w-md scroll-mt-24 border-0 bg-transparent text-left shadow-none">
      <CardContent className="p-0">
        {submitted ? (
          <div className="flex flex-col items-center text-center gap-4 py-10">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <CheckCircle2 className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-xl font-semibold">Thanks, {fullName.split(' ')[0]}.</h3>
            <p className="text-sm text-muted-foreground max-w-xs">
              We'll reach out at <span className="font-medium text-foreground">{email}</span> within
              a day to set up your plan.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <p className="text-sm text-muted-foreground">
              A real person reaches out within a day to set up your plan. No payment, no commitment.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="intake-name">Full name</Label>
              <Input
                id="intake-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jordan Rivera"
                autoComplete="name"
                maxLength={120}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="intake-email">Email</Label>
              <Input
                id="intake-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jordan@example.com"
                autoComplete="email"
                maxLength={255}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="intake-phone">Phone <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                id="intake-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                autoComplete="tel"
                maxLength={30}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="intake-grade">Grade</Label>
                <Select value={gradeLevel} onValueChange={setGradeLevel}>
                  <SelectTrigger id="intake-grade" aria-label="Select grade level">
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent>
                    {GRADE_OPTIONS.map((g) => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="intake-test-date">Target SAT date</Label>
                <Select value={targetTestDate} onValueChange={setTargetTestDate}>
                  <SelectTrigger id="intake-test-date" aria-label="Select target SAT date">
                    <SelectValue placeholder="Select date" />
                  </SelectTrigger>
                  <SelectContent>
                    {TEST_DATE_OPTIONS.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="intake-referral">Referral code <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                id="intake-referral"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                placeholder="Got a code from a friend or tutor?"
                maxLength={60}
              />
            </div>
            {/* Honeypot — hidden from humans, bots fill it and get dropped */}
            <div className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor="intake-website">Website</label>
              <input
                id="intake-website"
                name="website"
                type="text"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            <Button type="submit" size="lg" className="w-full" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send my details
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">
              We only use this to set up your prep.
            </p>
            <p className="text-xs text-center text-muted-foreground border-t border-border pt-3">
              Want to start on your own?{' '}
              <Link to={COURSE_HREF} className="font-semibold text-primary">
                Start the free course →
              </Link>
            </p>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
